// OperationTypeIcon.tsx
import React from 'react';
import {
  UserAddOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  FileSearchOutlined
} from '@ant-design/icons';
import { Space, Typography } from 'antd';
import styled from 'styled-components';

const { Text } = Typography;

export type OperationType = 'CREATE_USER' | 'DEPOSIT' | 'WITHDRAW' | 'BALANCE_CHECK';

interface OperationTypeIconProps {
  type: OperationType;
  showLabel?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const IconContainer = styled.div<{ $color: string; $size: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${props => {
    switch (props.$size) {
      case 'small': return '28px';
      case 'large': return '48px';
      default: return '36px';
    }
  }};
  height: ${props => {
    switch (props.$size) {
      case 'small': return '28px';
      case 'large': return '48px';
      default: return '36px';
    }
  }};
  border-radius: 8px;
  background: ${props => props.$color}15;
  border: 2px solid ${props => props.$color}40;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.$color}25;
    border-color: ${props => props.$color}60;
    transform: scale(1.05);
  }

  .anticon {
    font-size: ${props => {
      switch (props.$size) {
        case 'small': return '14px';
        case 'large': return '24px';
        default: return '18px';
      }
    }};
    color: ${props => props.$color};
  }
`;

const getOperationConfig = (type: OperationType) => {
  switch (type) {
    case 'CREATE_USER':
      return {
        icon: UserAddOutlined,
        color: '#1890ff',
        label: 'User Creation',
        labelEs: 'Creación de Usuario'
      };
    case 'DEPOSIT':
      return {
        icon: ArrowUpOutlined,
        color: '#52c41a',
        label: 'Deposit',
        labelEs: 'Depósito'
      };
    case 'WITHDRAW':
      return {
        icon: ArrowDownOutlined,
        color: '#fa8c16',
        label: 'Withdraw',
        labelEs: 'Retiro'
      };
    case 'BALANCE_CHECK':
      return {
        icon: FileSearchOutlined,
        color: '#722ed1',
        label: 'Balance Check',
        labelEs: 'Verificación de Saldo'
      };
    default:
      return {
        icon: FileSearchOutlined,
        color: '#8c8c8c',
        label: 'Unknown',
        labelEs: 'Desconocido'
      };
  }
};

export const OperationTypeIcon: React.FC<OperationTypeIconProps> = ({
  type,
  showLabel = false,
  size = 'medium'
}) => {
  const config = getOperationConfig(type);
  const Icon = config.icon;

  if (showLabel) {
    return (
      <Space size="small">
        <IconContainer $color={config.color} $size={size}>
          <Icon />
        </IconContainer>
        <Text strong style={{ color: config.color }}>
          {config.labelEs}
        </Text>
      </Space>
    );
  }

  return (
    <IconContainer $color={config.color} $size={size}>
      <Icon />
    </IconContainer>
  );
};

export const getOperationTypeColor = (type: OperationType): string => {
  return getOperationConfig(type).color;
};

export const getOperationTypeLabel = (type: OperationType): string => {
  return getOperationConfig(type).labelEs;
};
